import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import type { ScheduleSlot } from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { deskScheduleOptions, errorMessage } from '@/features/desks/desks-queries';
import { DeskRowAction } from '@/features/desks/desk-stages-section';
import { hhmmToMinute, minuteToHHMM } from '@/features/desks/desk-time';

const WEEKDAYS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

/** Faixa em edição: horários "HH:MM"; fim "00:00" significa meia-noite (1440). */
interface RangeDraft {
  start: string;
  end: string;
}

function toDrafts(slots: ScheduleSlot[]): RangeDraft[][] {
  const byDay: RangeDraft[][] = Array.from({ length: 7 }, () => []);
  for (const slot of [...slots].sort((a, b) => a.startMinute - b.startMinute)) {
    byDay[slot.weekday]?.push({
      start: minuteToHHMM(slot.startMinute),
      end: minuteToHHMM(slot.endMinute),
    });
  }
  return byDay;
}

export function DeskScheduleSection({ deskId }: { deskId: number }) {
  const schedule = useQuery(deskScheduleOptions(deskId));

  return (
    <section className="rounded-lg border bg-card p-4 shadow-xs">
      <div className="mb-1 flex items-center gap-2">
        <h2 className="text-base font-semibold">Expediente</h2>
      </div>
      <p className="mb-3 text-[13px] text-muted-foreground">
        Faixas de atendimento por dia da semana. Sem nenhuma faixa cadastrada, o SLA conta em
        horas corridas (24/7). Use 00:00 no fim da faixa para indicar meia-noite.
      </p>
      {schedule.isPending ? (
        <div className="space-y-2" aria-busy>
          {Array.from({ length: 7 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      ) : schedule.isError ? (
        <EmptyState
          className="h-24"
          message={`Erro ao carregar expediente: ${errorMessage(schedule.error)}`}
        />
      ) : (
        <ScheduleEditor key={deskId} deskId={deskId} initial={schedule.data} />
      )}
    </section>
  );
}

function ScheduleEditor({ deskId, initial }: { deskId: number; initial: ScheduleSlot[] }) {
  const queryClient = useQueryClient();
  const [days, setDays] = useState<RangeDraft[][]>(() => toDrafts(initial));
  const [invalidMsg, setInvalidMsg] = useState<string | null>(null);

  const dirty = useMemo(
    () => JSON.stringify(days) !== JSON.stringify(toDrafts(initial)),
    [days, initial]
  );

  const save = useMutation({
    mutationFn: async (slots: ScheduleSlot[]) => {
      const res = await api<ScheduleSlot[]>(`/desks/${deskId}/schedule`, {
        method: 'PUT',
        body: { slots },
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['erp', 'desks', 'schedule', deskId] });
    },
  });

  const updateRange = (weekday: number, index: number, patch: Partial<RangeDraft>) => {
    setDays((prev) =>
      prev.map((ranges, d) =>
        d === weekday ? ranges.map((r, i) => (i === index ? { ...r, ...patch } : r)) : ranges
      )
    );
  };

  const addRange = (weekday: number) => {
    setDays((prev) =>
      prev.map((ranges, d) =>
        d === weekday ? [...ranges, { start: '08:00', end: '18:00' }] : ranges
      )
    );
  };

  const removeRange = (weekday: number, index: number) => {
    setDays((prev) =>
      prev.map((ranges, d) => (d === weekday ? ranges.filter((_, i) => i !== index) : ranges))
    );
  };

  const submit = () => {
    const slots: ScheduleSlot[] = [];
    for (let weekday = 0; weekday < 7; weekday++) {
      for (const range of days[weekday] ?? []) {
        const startMinute = hhmmToMinute(range.start);
        const endMinute = hhmmToMinute(range.end, { endOfDay: true });
        if (startMinute === null || endMinute === null || endMinute <= startMinute) {
          setInvalidMsg(
            `Faixa inválida em ${WEEKDAYS[weekday]}: o início deve ser antes do fim.`
          );
          return;
        }
        slots.push({ weekday, startMinute, endMinute });
      }
    }
    setInvalidMsg(null);
    save.mutate(slots);
  };

  return (
    <div>
      <ul className="divide-y divide-divider">
        {WEEKDAYS.map((label, weekday) => {
          const ranges = days[weekday] ?? [];
          return (
            <li key={weekday} className="flex items-start gap-3 py-2">
              <span className="w-24 shrink-0 pt-1.5 text-sm font-medium">{label}</span>
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                {ranges.length === 0 ? (
                  <span className="py-1.5 text-[13px] text-muted-foreground">Sem expediente</span>
                ) : (
                  ranges.map((range, i) => (
                    <span
                      key={i}
                      className="flex items-center gap-1 rounded-md border border-divider px-1.5 py-1"
                    >
                      <input
                        type="time"
                        value={range.start}
                        aria-label={`${label}: início da faixa ${i + 1}`}
                        onChange={(e) => updateRange(weekday, i, { start: e.target.value })}
                        className="h-7 rounded-sm bg-transparent px-1 text-sm tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                      />
                      <span className="text-muted-foreground">–</span>
                      <input
                        type="time"
                        value={range.end}
                        aria-label={`${label}: fim da faixa ${i + 1}`}
                        onChange={(e) => updateRange(weekday, i, { end: e.target.value })}
                        className="h-7 rounded-sm bg-transparent px-1 text-sm tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                      />
                      <DeskRowAction
                        label={`Remover faixa ${i + 1} de ${label}`}
                        destructive
                        onClick={() => removeRange(weekday, i)}
                      >
                        <Trash2 className="size-3.5" />
                      </DeskRowAction>
                    </span>
                  ))
                )}
                <DeskRowAction label={`Adicionar faixa em ${label}`} onClick={() => addRange(weekday)}>
                  <Plus className="size-3.5" />
                </DeskRowAction>
              </div>
            </li>
          );
        })}
      </ul>
      {invalidMsg ? <p className="mt-2 text-sm text-destructive">{invalidMsg}</p> : null}
      {save.error ? <p className="mt-2 text-sm text-destructive">{errorMessage(save.error)}</p> : null}
      <div className="mt-3 flex items-center justify-end gap-3 border-t border-divider pt-3">
        {dirty && !save.isPending ? (
          <span className="text-xs text-muted-foreground">Alterações não salvas</span>
        ) : null}
        <Button size="sm" onClick={submit} disabled={save.isPending || !dirty}>
          {save.isPending ? 'Salvando…' : 'Salvar expediente'}
        </Button>
      </div>
    </div>
  );
}
